import React, { useEffect, useState, useContext } from 'react';
import { Card, CardBody, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { PackagesService } from './service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Select from 'react-select';
import { EnumsContext } from '../../context/EnumsContext';
import { useTranslation } from 'react-i18next';
import Pagination from '../../Common/Pagination';

const PackagesList = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [term, setTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<number | null>(null);
  const navigate = useNavigate();
  const { enums, loading } = useContext(EnumsContext);
  const packageType = enums?.packageType?.map((type: any) => ({
    label: type[0].toUpperCase() + type.slice(1),
    value: type,
  }));

  const entriesPerPageOptions = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '30', value: 30 },
    { label: '40', value: 40 },
  ];

  const handleTermChange = (e: any) => {
    setTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (selectedOption: any) => {
    setSelectedType(selectedOption ? selectedOption.value : null);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteClick = (id: number) => {
    setPackageToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (packageToDelete !== null) {
      try {
        await PackagesService.delete(packageToDelete);
        SuccessToast(t('packages.successDelete'));
        setShowDeleteModal(false);
        setPackageToDelete(null);
        fetchData();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  const fetchData = async () => {
    try {
      const response = await PackagesService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
        term,
        type: selectedType, // Pass the selected type as a filter
      });
      const data = response.data;
      setList(data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage, term, selectedType]);

  if (loading) {
    return <div>{t('packages.loading')}</div>;
  }

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className="text-end">
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate('/packages/create')}
              >
                <i className="ti ti-plus f-18"></i> {t('packages.addPackage')}
              </button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('packages.entriesPerPage')}
                    options={entriesPerPageOptions}
                    onChange={handleEntriesPerPageChange}
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === entriesPerPage
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t('packages.entriesPerPage')}</span>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('packages.filterByType')}
                    options={packageType}
                    onChange={handleTypeFilterChange}
                    isClearable
                    classNamePrefix="react-select"
                  />
                </li>
                <li className="list-inline-item">
                  <form className="">
                    <input
                      type="search"
                      className="form-control"
                      placeholder={t('packages.searchPlaceholder')}
                      onChange={handleTermChange}
                    />
                  </form>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
                <thead>
                  <tr>
                    <th>{t('packages.id')}</th>
                    <th>{t('packages.name')}</th>
                    <th>{t('packages.type')}</th>
                    <th>{t('packages.active')}</th>
                    <th className="text-end sticky-col">{t('packages.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list?.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item.id}</td>
                      <td className="wrap-col">{item.name || 'N/A'}</td>
                      <td>{item.type ? item.type[0].toUpperCase() + item.type.slice(1) : 'N/A'}</td>
                      <td>{item.isActive ? t('packages.yes') : t('packages.no')}</td>
                      <td className="text-end text-nowrap sticky-col">
                        <ul className="list-inline mb-0">
                          <li
                            className="list-inline-item"
                            onClick={() => navigate(`/packages/edit/${item.id}`)}
                          >
                            <a
                              href="#"
                              className="avtar avtar-s btn-link-success btn-pc-default"
                            >
                              <i className="ti ti-edit f-20"></i>
                            </a>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              previousLabel={t('packages.previous')}
              nextLabel={t('packages.next')}
            />
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name="package"
      />
    </React.Fragment>
  );
};

export default PackagesList;